import React, { act, useEffect, useMemo, useState } from 'react';
import { getFirestore } from 'firebase/firestore';
import '../Investigations/investigations.css';
import LoaderWheel from '../../components/common/LoaderWheel'; 
import { ref, listAll, getDownloadURL } from 'firebase/storage';
import { FIREBASE_STORAGE } from '../../firebase/firebase.js';

interface PoliciesProps {
    hasAdminAccess: boolean;
    activeUser: string;
}
const Policies: React.FC<PoliciesProps> = ({ hasAdminAccess, activeUser }) => {
        const [pdfFiles, setPdfFiles] = useState<{name: string, path: string, downloadURL: string, folder: string}[]>([]);
        const [selectedPdf, setSelectedPdf] = useState<string | null>(null);
        const [loading, setLoading] = useState(true);
        const [error, setError] = useState<string>('');

        useEffect(() => {
            async function loadPdfFilesFromFirebase() {
                try {
                    setLoading(true);
                    setError('');

                    let allPdfFiles: {name: string, path: string, downloadURL: string, folder: string}[] = [];

                    // Always load general policies
                    try {
                        const generalPoliciesRef = ref(FIREBASE_STORAGE, 'policies/general');
                        const generalResult = await listAll(generalPoliciesRef);
                        
                        const generalPdfPromises = generalResult.items
                            .filter(item => item.name.toLowerCase().endsWith('.pdf'))
                            .map(async (item) => {
                                const downloadURL = await getDownloadURL(item);
                                return {
                                    name: item.name.replace('.pdf', ''),
                                    path: item.fullPath,
                                    downloadURL: downloadURL,
                                    folder: 'General'
                                };
                            });

                        const generalPdfFiles = await Promise.all(generalPdfPromises);
                        allPdfFiles = [...allPdfFiles, ...generalPdfFiles];
                    } catch (generalError) {
                        console.warn('Could not load general policies:', generalError);
                    }

                    // If admin, also load admin policies
                    if (hasAdminAccess) {
                        try {
                            const adminPoliciesRef = ref(FIREBASE_STORAGE, 'policies/admin');
                            const adminResult = await listAll(adminPoliciesRef);
                            
                            const adminPdfPromises = adminResult.items
                                .filter(item => item.name.toLowerCase().endsWith('.pdf'))
                                .map(async (item) => {
                                    const downloadURL = await getDownloadURL(item);
                                    return {
                                        name: item.name.replace('.pdf', ''),
                                        path: item.fullPath,
                                        downloadURL: downloadURL,
                                        folder: 'Admin'
                                    };
                                });

                            const adminPdfFiles = await Promise.all(adminPdfPromises);
                            allPdfFiles = [...allPdfFiles, ...adminPdfFiles];
                        } catch (adminError) {
                            console.warn('Could not load admin policies:', adminError);
                        }
                    }
                    
                    // Sort files: Admin policies first, then General, then alphabetically by name within each group
                    allPdfFiles.sort((a, b) => {
                        if (a.folder !== b.folder) {
                            return a.folder === 'Admin' ? -1 : 1;
                        }
                        return a.name.localeCompare(b.name);
                    });
                    allPdfFiles.sort((a, b) => a.name.localeCompare(b.name));
                    setPdfFiles(allPdfFiles);
                    
                    if (allPdfFiles.length === 0) {
                        setError('No policy documents found for your access level.');
                    }
                    
                } catch (error) {
                    console.error('Error loading PDF files from Firebase:', error);
                    setError('Failed to load policy documents. Please try again later.');
                    setPdfFiles([]);
                } finally {
                    setLoading(false);
                }
            }
            
            // Only load files if user authentication is determined
            if (activeUser) {
                loadPdfFilesFromFirebase();
            }
        }, [hasAdminAccess, activeUser]);

        const handlePdfClick = (downloadURL: string) => {
            setSelectedPdf(downloadURL);
        };

        const handleBackToList = () => {
            setSelectedPdf(null);
        };

        if (loading) {
            return (
                <div className="audit-table-container">
                    <h1 className="audit-table-title">Policies</h1>
                    <div className="loading-spinner">
                        <div className="spinner"></div>
                    </div>
                </div>
            );
        }

        if (error) {
            return (
                <div className="audit-table-container">
                    <h1 className="audit-table-title">Policies</h1>
                    <div className="no-data-message">
                        {error}
                    </div>
                </div>
            );
        }

        return (
            <div className="audit-table-container policies-flex">
                <div className="policies-selector">
                    <h1 className="audit-table-title">
                        Policies {hasAdminAccess ? '(Admin + General Access)' : '(General Access)'}
                    </h1>
                    {pdfFiles.length === 0 ? (
                        <div className="no-data-message">
                            No policy documents found for your access level.
                        </div>
                    ) : (
                        <table className="audit-table">
                            <thead>
                                <tr>
                                    <th>Policy Document</th>
                                    <th>Category</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pdfFiles.map((pdf, index) => {
                                    const isActive = selectedPdf === pdf.downloadURL;
                                    return (
                                        <tr
                                            key={pdf.path || `${pdf.folder}-${pdf.name}-${index}`}
                                            className={`pdf-row${isActive ? ' pdf-row-active' : ''}`}
                                            onClick={() => handlePdfClick(pdf.downloadURL)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <td>
                                                📄 {pdf.name}
                                            </td>
                                            <td>
                                                <span className={pdf.folder === 'Admin' ? 'admin-badge' : 'general-badge'}>
                                                    {pdf.folder}
                                                </span>
                                            </td>
                                            <td>
                                                <a
                                                    href={pdf.downloadURL}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="addAuditEntry pdf-download-link"
                                                    onClick={e => e.stopPropagation()} // Prevents row click from firing
                                                >
                                                    Download
                                                </a>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
                <div className="pdf-viewer-container policies-viewer">
                    {selectedPdf ? (
                        <div className="policies-viewer-frame">
                            <button
                                onClick={handleBackToList}
                                className="policies-viewer-back"
                            >
                                Back
                            </button>
                            <iframe
                                src={selectedPdf + "#view=FitH"}
                                title="Policy PDF"
                                className="policies-viewer-iframe"
                            />
                        </div>
                    ) : (
                        <div className="policies-viewer-placeholder">
                            Select a policy document to view.
                        </div>
                    )}
                </div>
            </div>
        );
    }
export default Policies;